import React, { useState } from 'react';
import { AlertTriangle, Trash2 } from 'lucide-react';
import Modal from '../ui/Modal';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { useAuthStore } from '../../store/authStore';

interface DeleteAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const DeleteAccountModal: React.FC<DeleteAccountModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { user, deleteAccount, isLoading } = useAuthStore();
  const [password, setPassword] = useState('');
  const [confirmationText, setConfirmationText] = useState('');
  const [step, setStep] = useState<'warning' | 'confirmation'>('warning');
  const [errors, setErrors] = useState<{ password?: string; confirmation?: string }>({});

  const handleClose = () => {
    setStep('warning');
    setPassword('');
    setConfirmationText('');
    setErrors({});
    onClose();
  };

  const handleContinue = () => {
    setStep('confirmation');
  };

  const validateForm = (): boolean => {
    const newErrors: { password?: string; confirmation?: string } = {};

    if (!password.trim()) {
      newErrors.password = 'Password is required';
    }

    if (!confirmationText.trim()) {
      newErrors.confirmation = 'Confirmation text is required';
    } else if (
      confirmationText !== user?.email &&
      confirmationText.toUpperCase() !== 'DELETE'
    ) {
      newErrors.confirmation = 'Please type your email address or "DELETE" to confirm';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleDeleteAccount = async () => {
    if (!validateForm()) return;

    try {
      await deleteAccount({
        password,
        confirmationText,
      });
      // The store will handle logout and navigation
      handleClose();
    } catch (error) {
      // Error is handled by the store
      console.error('Account deletion failed:', error);
    }
  };

  if (!user) return null;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="">
      <div className="p-6">
        {step === 'warning' && (
          <>
            <div className="flex items-center mb-4">
              <AlertTriangle className="w-6 h-6 text-red-500 mr-3" />
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                Delete Account
              </h2>
            </div>

            <div className="mb-6">
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-4">
                <h3 className="font-medium text-red-800 dark:text-red-200 mb-2">
                  ⚠️ This action cannot be undone
                </h3>
                <p className="text-red-700 dark:text-red-300 text-sm">
                  Deleting your account will permanently remove all your data, including:
                </p>
                <ul className="list-disc list-inside text-red-700 dark:text-red-300 text-sm mt-2 space-y-1">
                  <li>All your habits and tracking history</li>
                  <li>Streak records and achievements</li>
                  <li>Notes and personal data</li>
                  <li>Subscription and billing information</li>
                  <li>Account settings and preferences</li>
                </ul>
              </div>

              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <h3 className="font-medium text-blue-800 dark:text-blue-200 mb-2">
                  30-Day Grace Period
                </h3>
                <p className="text-blue-700 dark:text-blue-300 text-sm">
                  Your account will be scheduled for deletion but you'll have 30 days to change your mind. 
                  During this time, you can log in and cancel the deletion to restore your account.
                </p>
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <Button
                variant="outline"
                onClick={handleClose}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={handleContinue}
                disabled={isLoading}
              >
                I Understand, Continue
              </Button>
            </div>
          </>
        )}

        {step === 'confirmation' && (
          <>
            <div className="flex items-center mb-4">
              <Trash2 className="w-6 h-6 text-red-500 mr-3" />
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                Confirm Account Deletion
              </h2>
            </div>

            <div className="mb-6">
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                To confirm account deletion, please enter your password and type either your 
                email address or "DELETE" in the confirmation field.
              </p>

              <div className="space-y-4">
                <Input
                  label="Current Password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  error={errors.password}
                  placeholder="Enter your current password"
                  disabled={isLoading}
                />

                <Input
                  label="Confirmation"
                  type="text"
                  value={confirmationText}
                  onChange={(e) => setConfirmationText(e.target.value)}
                  error={errors.confirmation}
                  placeholder={`Type "${user.email}" or "DELETE"`}
                  disabled={isLoading}
                />
              </div>

              <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  <strong>Account:</strong> {user.email}
                  <br />
                  <strong>Created:</strong> {new Date(user.createdAt).toLocaleDateString()}
                </p>
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <Button
                variant="outline"
                onClick={() => setStep('warning')}
                disabled={isLoading}
              >
                Back
              </Button>
              <Button
                variant="outline"
                onClick={handleClose}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={handleDeleteAccount}
                disabled={isLoading}
                isLoading={isLoading}
              >
                Delete My Account
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};
